/**
 * @author      calvin@zumper.com (Calvin Yau)
 * @copyright   Copyright (c) 2019, Zumper
 * @description getLocationFromAutocompleteResult thunk
 */

import type { AutocompleteResult } from '@zcode/bizos'
import { ZOOM_ADDRESS, definitelyError } from '@zcode/nr-utils'
import { logger } from '../../../../logger'
import { locate } from '../../../../shared/api/geos'
import {
  AUTOCOMPLETE_BUILDING,
  AUTOCOMPLETE_CITY,
  AUTOCOMPLETE_NEIGHBORHOOD,
  AUTOCOMPLETE_POSTCODE,
  NEW_YORK_BOROUGHS_KEY_ARRAY,
  NEW_YORK_BOROUGHS_URLS,
} from '../../../../shared/components/Autocomplete/constants'
import getLocationFromGeos from '../../../../shared/utils/location/getLocationFromGeos'
import { toSlug } from './toSlug'
import { trackAutocompleteGeosMismatch } from './trackAutocompleteGeosMismatch'

const countryCodes = {
  USA: 'us',
  CAN: 'ca',
} as const

const isNYBorough = (hoodSlug?: string): hoodSlug is keyof typeof NEW_YORK_BOROUGHS_URLS => {
  return typeof hoodSlug === 'string' && NEW_YORK_BOROUGHS_KEY_ARRAY.includes(hoodSlug)
}

const locateWithLatLng = async ({
  box,
  city,
  country,
  lat,
  lng,
  hoodsLimit,
  height,
  name,
  width,
  type,
  url,
}: {
  city: string | null | undefined
  height: number | null | undefined
  hoodsLimit: number | undefined
  lat: number
  lng: number
  name: string | null | undefined
  url: string
  width: number | null | undefined
} & Partial<Omit<AutocompleteResult, 'lat' | 'lng'>>) => {
  try {
    const isBorough = isNYBorough(url)
    const convertedUrl = isBorough ? NEW_YORK_BOROUGHS_URLS[url] : url
    const convertedType = isBorough ? AUTOCOMPLETE_CITY : type
    const geos = await locate({ lat, lng, hoodsLimit, url: convertedUrl })

    const citySlug = convertedType === AUTOCOMPLETE_CITY ? convertedUrl : ''
    const hoodSlug =
      convertedType === AUTOCOMPLETE_NEIGHBORHOOD || convertedType === AUTOCOMPLETE_POSTCODE
        ? convertedUrl
        : ''

    const location = getLocationFromGeos({
      box,
      citySlug,
      geos,
      height,
      hoodSlug,
      lat,
      lng,
      type: convertedType,
      width,
    })

    if (location.lat === undefined || location.lng === undefined) {
      location.lat = lat
      location.lng = lng
    }
    if (type === AUTOCOMPLETE_BUILDING) {
      location.zoom = ZOOM_ADDRESS
    }
    if (country) {
      location.country = countryCodes[country as keyof typeof countryCodes] ?? 'us'
    }

    if (url !== location.url) {
      trackAutocompleteGeosMismatch(
        {
          box,
          city,
          lat,
          lng,
          name,
          type,
          url,
        },
        location
      )
    }

    return await Promise.resolve({ locationObj: location })
  } catch (error) {
    const message = 'failed to locate geos with lat and lng'
    const e = definitelyError(message, error)
    logger.warn(message)
    return await Promise.reject(e)
  }
}

const getLocationFromAutocompleteResult = async ({
  hoodsLimit,
  result,
  height,
  width,
}: {
  height?: number | null
  hoodsLimit?: number
  result: AutocompleteResult
  width?: number | null
}) => {
  const { box, city, country, hood, lat, lng, name, state, type } = result
  const slug = result.url || toSlug(city, state, hood)

  if (!lat || !lng) {
    return await Promise.reject(new Error('autocomplete result missing lat and/or lng'))
  }

  return await locateWithLatLng({
    box,
    city,
    country,
    lat,
    lng,
    hoodsLimit,
    height,
    name,
    width,
    type,
    url: slug,
  })
}

export default getLocationFromAutocompleteResult
